import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, Package, Settings, Bot } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  createProduct,
  updateProduct,
  listProductStatusTypes,
  type Product,
  type ProductStatusType,
} from "@/services/productsService";
import { listAIModels, getAIModelById } from "@/services/aiModelsService";
import type { AIModel } from "@/types/aiModels";
import { listAgents, type Agent } from "@/services/agentsService";

interface ProductFormProps {
  onBack: () => void;
  onSave: () => void;
  editingProduct: Product | null;
}

interface FormData {
  name: string;
  description: string;
  model_id: string;
  agent_id: string;
  status_id: number;
}

export const ProductForm = ({
  onBack,
  onSave,
  editingProduct,
}: ProductFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    model_id: "",
    agent_id: "",
    status_id: 1,
  });
  const [models, setModels] = useState<AIModel[]>([]);
  const [unavailableSelectedModel, setUnavailableSelectedModel] =
    useState<AIModel | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [statusTypes, setStatusTypes] = useState<ProductStatusType[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        name: editingProduct.name,
        description: editingProduct.description || "",
        model_id: editingProduct.model_id,
        agent_id: editingProduct.agent_id,
        status_id: editingProduct.status?.id || editingProduct.status_id,
      });
    }
  }, [editingProduct]);

  useEffect(() => {
    const syncSelectedModelAvailability = async () => {
      if (!formData.model_id) {
        setUnavailableSelectedModel(null);
        return;
      }

      const availableModel = models.find(
        (model) => model.id === formData.model_id
      );
      if (availableModel) {
        setUnavailableSelectedModel(null);
        return;
      }

      if (!editingProduct || editingProduct.model_id !== formData.model_id) {
        setUnavailableSelectedModel(null);
        return;
      }

      const response = await getAIModelById(formData.model_id);
      if (response.success && response.data) {
        setUnavailableSelectedModel(response.data);
      } else {
        setUnavailableSelectedModel(null);
      }
    };

    syncSelectedModelAvailability();
  }, [formData.model_id, models, editingProduct]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [modelsRes, agentsRes, statusTypesRes] = await Promise.all([
        listAIModels({
          limit: 100,
          is_active: true,
          is_integration_active: true,
        }),
        listAgents({ limit: 100 }),
        listProductStatusTypes(),
      ]);

      if (modelsRes.success && modelsRes.data) {
        setModels(modelsRes.data.models);
      }
      if (agentsRes.success && agentsRes.data) {
        setAgents(agentsRes.data.agents);
      }
      if (statusTypesRes.success && statusTypesRes.data) {
        setStatusTypes(
          statusTypesRes.data.data || statusTypesRes.data
        );
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Nome do especialista é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.model_id) {
      toast({
        title: "Erro de Validação",
        description: "Selecione um modelo de IA",
        variant: "destructive",
      });
      return;
    }

    const selectedModel =
      models.find((model) => model.id === formData.model_id) ||
      (unavailableSelectedModel?.id === formData.model_id
        ? unavailableSelectedModel
        : null);
    if (
      !selectedModel ||
      !selectedModel.is_active ||
      !selectedModel.is_integration_active
    ) {
      toast({
        title: "Modelo indisponível",
        description:
          "O modelo selecionado não está disponível. Escolha um modelo com integração ativa.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.agent_id) {
      toast({
        title: "Erro de Validação",
        description: "Selecione um agente",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (editingProduct) {
        const updateData = {
          name: formData.name,
          description: formData.description,
          model_id: formData.model_id,
          agent_id: formData.agent_id,
          status_id: formData.status_id,
        };

        const response = await updateProduct(editingProduct.id, updateData);

        if (response.success) {
          toast({
            title: "Sucesso",
            description: "Especialista atualizado com sucesso",
          });
          onSave();
        } else {
          throw new Error(
            response.error?.message || "Erro ao atualizar especialista"
          );
        }
      } else {
        const response = await createProduct({
          name: formData.name,
          description: formData.description,
          model_id: formData.model_id,
          agent_id: formData.agent_id,
          status_id: formData.status_id,
        });

        if (response.success) {
          toast({
            title: "Sucesso",
            description: "Especialista criado com sucesso",
          });
          onSave();
        } else {
          throw new Error(response.error?.message || "Erro ao criar especialista");
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  const publishedAgents = agents.filter((agent) => {
    if (!agent.active_version_id) {
      return false;
    }
    const activeVersion = agent.versions?.find(
      (v) => v.id === agent.active_version_id
    );
    return activeVersion?.is_published === true;
  });

  const translateStatusShort = (statusName?: string) => {
    if (!statusName) return "Desconhecido";
    const key = statusName.toLowerCase().trim();
    const map: Record<string, string> = {
      active: "Ativo",
      inactive: "Inativo",
      development: "Em desenvolvimento",
      deprecated: "Descontinuado",
    };
    const translated = map[key] || statusName;
    return translated.charAt(0).toUpperCase() + translated.slice(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Package}
        title={editingProduct ? "Editar Especialista" : "Novo Especialista"}
        description="Configure um especialista conectando um modelo de IA com um agente especializado"
        onBack={onBack}
      />

      <main className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {editingProduct ? "Editar Especialista" : "Novo Especialista"}
            </CardTitle>
            <CardDescription>
              Configure um especialista conectando um modelo de IA com um agente
              especializado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome do Especialista *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Ex: Geração de Imagens IA"
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descreva o propósito e funcionalidades do especialista"
                  rows={3}
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label>Modelo de IA *</Label>
                {models.length === 0 && !unavailableSelectedModel ? (
                  <>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Nenhum modelo disponível" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-models" disabled>
                          Nenhum modelo com integração ativa
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Nenhum modelo com integração ativa está disponível. Ative a
                      integração de um modelo no painel de Modelos de IA.
                    </p>
                  </>
                ) : (
                  <>
                    <Select
                      value={formData.model_id}
                      onValueChange={(value) => {
                        if (
                          unavailableSelectedModel &&
                          value === unavailableSelectedModel.id
                        ) {
                          toast({
                            title: "Modelo indisponível",
                            description:
                              "O modelo selecionado não está disponível.",
                            variant: "destructive",
                          });
                          return;
                        }
                        const selectedModel = models.find(
                          (model) => model.id === value
                        );
                        if (
                          !selectedModel ||
                          !selectedModel.is_integration_active
                        ) {
                          toast({
                            title: "Modelo indisponível",
                            description:
                              "O modelo selecionado não está disponível.",
                            variant: "destructive",
                          });
                          return;
                        }
                        setUnavailableSelectedModel(null);
                        setFormData({ ...formData, model_id: value });
                      }}
                      disabled={saving}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um modelo" />
                      </SelectTrigger>
                      <SelectContent>
                        {unavailableSelectedModel && (
                          <SelectItem
                            value={unavailableSelectedModel.id}
                            disabled
                          >
                            <div className="flex items-center text-muted-foreground">
                              <Settings className="h-4 w-4 mr-2" />
                              {unavailableSelectedModel.name} (indisponível)
                            </div>
                          </SelectItem>
                        )}
                        {models.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center">
                              <Settings className="h-4 w-4 mr-2" />
                              {model.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {unavailableSelectedModel && (
                      <p className="text-sm text-destructive">
                        O modelo atual não está disponível. Selecione um modelo
                        com integração ativa.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="grid gap-2">
                <Label>Agente *</Label>
                {publishedAgents.length === 0 ? (
                  <>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Nenhum agente disponível" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-agents" disabled>
                          Nenhum agente cadastrado e publicado
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Nenhum agente cadastrado e publicado
                    </p>
                  </>
                ) : (
                  <Select
                    value={formData.agent_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, agent_id: value })
                    }
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um agente" />
                    </SelectTrigger>
                    <SelectContent>
                      {publishedAgents.map((agent) => {
                        const agentText = `${agent.name}${agent.type_name ? ` (${agent.type_name})` : ""}${agent.active_version ? ` - v${agent.active_version}` : ""}`;
                        return (
                          <SelectItem key={agent.id} value={agent.id}>
                            <div className="flex items-center">
                              <Bot className="h-4 w-4 mr-2" />
                              <span>{agentText}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid gap-2">
                <Label>Status *</Label>
                <Select
                  value={formData.status_id.toString()}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status_id: parseInt(value) })
                  }
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusTypes
                      .filter((status) => status.is_active)
                      .map((status) => (
                        <SelectItem
                          key={status.id}
                          value={status.id.toString()}
                        >
                          {translateStatusShort(status.name || status.description)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button variant="outline" onClick={onBack} disabled={saving}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-primary text-white hover:opacity-90"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingProduct ? "Atualizar" : "Criar Especialista"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

